import { Injectable } from '@angular/core';
import {
  BaseSubmissionModel,
  AddressDetails,
  Party,
  FrequencyUnitDetails,
} from '../models/submission/base-submission-model';
import { environment } from '../../environments/environment';
import Decimal from 'decimal.js';
import _ from 'lodash';
import * as uuid from 'uuid';
import { DEFAULTS } from '../_global/wiz.constants';

const COMPANY = 'company';
const PERSON = 'person';
const TRUST = 'trust';
const MORTGAGOR = 'mortgagor';

export interface SubmissionDefaults {
  purposeType: string;
  salesRelatesTo: string; // to be stored in relates to section of the lead
  channel: string; // to be stored in relates to section of the lead
  shop: string; // to be stored in relates to section of the lead
}

/**
 * Here we convert the Wizard Widgets to submission-API shape "A" object
 * https://git.firstmac.com.au/apis/submission-api/tree/develop/models
 */
@Injectable()
export class SubmissionService {
  constructor() { }

  /**
   * Declare all widgets here
   */
  private widgetMapper = {
    'app-originator-broker': this.mapOriginatorBroker,
    'app-customer-details': this.mapCustomerDetails,
    'app-abn-acn': this.mapAbnAcn,
    'app-trust-details': this.mapTrustDetails,
    'app-company-details': this.mapCompanyDetails,
    'app-personal-details': this.mapPersonalDetails.bind(this),
    'app-borrower-type': this.mapBorrowerType,
    'app-borrower-type-enhanced': this.mapBorrowerTypeEnhanced,
    'app-home-loan-details': this.mapHomeLoanDetails,
    'app-loan-details': this.mapLoanDetails,
    'app-single-split-loan-amount': this.mapSingleSplitLoanAmount,
    'app-balloon-amount': this.mapBalloonAmount,
    'app-loan-terms': this.mapLoanTerms,
    'app-auto-collateral': this.mapAutoCollateral,
    'app-submit': this.mapSubmit,
    'app-employment-details': this.mapEmployments,
    'app-income-details': this.mapCompanyIncomes,
    'app-assets-details': this.mapAssets,
    'app-liabilities-details': this.mapLiabilities,
    'app-expenses-details': this.mapExpenses,
    'app-investment-income': this.mapInvestmentIncomes,
    'app-loan-details-terms': this.mapTermDetails.bind(this),
    'app-quote-details': this.mapQuoteDetails.bind(this),
    'app-quote-results': this.mapQuoteResults,
    'app-application-type': this.mapApplicationType,
    'app-security-details': this.mapSecurityDetails,
    'app-solicitor-panel-details': this.mapPanelSolicitorDetails,
    'app-solicitor-borrower-details': this.mapBorrowerSolicitorDetails,
    'app-rate': this.mapRateComponent,
    'app-broker-notes': this.mapBrokerNotes,
    'app-product': this.mapProduct,
    'app-docs-upload': this.mapDocsToUpload,
  };

  static mapAddressDetails(address, occupancy = null, residencyType = null): AddressDetails {
    if (address && (address.flatSearch || address.fullAddress)) {
      console.log('mapAddressDetails address', address);

      const addr: AddressDetails = {
        addressId: (address.addressId) ? +(address.addressId) : null,
        country: address.countrySlug,
        propertyName: address.propertyName,
        unitNumber: address.unitNumber,
        streetNumber: address.streetNumber,
        streetName: address.streetName,
        streetType: address.streetTypeSlug || 'street', // default this to street to avoid errors
        suburb: address.suburb,
        postcode: address.postcode,
        state: address.stateSlug || address.state || '', // state can be manually filled up if country != AUS, // set to empty string('') if stateSlug and state are undefined/null, null value for state is not allowed in our submission-api schema.
        poBox: address.poBox,
        rpId: address.rPDataPropertyID,
        addressText: address.flatSearch ? address.flatSearch : address.fullAddress,
      };
      if (occupancy) {
        addr['occupancy'] = {
          unit: 'months',
          value: occupancy,
        };
      }
      if (residencyType) {
        addr['residencyType'] = residencyType;
      }
      return addr;
    }
    return undefined;
  }

  static getIndividualData(type, id, data: any, submission?) {
    const newData = {
      id,
      type: 'individual',
      otherNames: [],
      contact: {
        mobile: '',
        email: '',
      },
    };
    let nameArr = [];

    console.group(':::getIndividualData:::');
    console.log('data', data);
    console.log('type', type);

    switch (type) {
      case 'trust':
        // When contact data for an individual is set to have "Same As" another entity
        if (data.contactDetailsAsEntity !== false && data.contactDetailsAsEntity !== null) {
          const entityDetailArr = data.contactDetailsAsEntity
            ? data.contactDetailsAsEntity.split(' ')
            : '';
          const entityType = entityDetailArr[0];
          const entityIndex = entityDetailArr[1];
          if (entityType && entityIndex) {
            // Get data to reference for "Same As" data
            const entity =
              submission[`${entityType}List`][+entityIndex > 0 ? Number(entityIndex) - 1 : 0];
            if (entity) {
              nameArr = entity[`${entityType}ContactName`]
                ? entity[`${entityType}ContactName`].split(' ')
                : '';
              newData['position'] = entity[`${entityType}ContactPosition`];
              newData.contact.mobile = entity[`${entityType}ContactMobile`]
                ? entity[`${entityType}ContactMobile`]
                : '';
              newData.contact.email = entity[`${entityType}ContactEmail`]
                ? entity[`${entityType}ContactEmail`]
                : '';
            }
          } else {
            throw new Error(
              '[ERR] contactDetailsAsEntity Invalid Format: ' + data.contactDetailsAsEntity
            );
          }
        } else {
          nameArr = data.trustContactName ? data.trustContactName.split(' ') : null;
          newData.contact.mobile = data.trustContactMobile ? data.trustContactMobile : '';
          newData.contact.email = data.trustContactEmail ? data.trustContactEmail : '';
          newData['position'] = data.trustContactPosition;
        }
        break;

      case 'company':
        // When contact data for an individual is set to have "Same As" another entity
        if (data.contactDetailsAsEntity !== false && data.contactDetailsAsEntity !== null) {
          const entityDetailArr = data.contactDetailsAsEntity.split(' ');
          const entityType = entityDetailArr[0];
          const entityIndex = entityDetailArr[1];
          if (entityType && entityIndex) {
            // Get data to reference for "Same As" data
            const entity =
              submission[`${entityType}List`][+entityIndex > 0 ? Number(entityIndex) - 1 : 0];
            if (entity) {
              nameArr = entity[`${entityType}ContactName`]
                ? entity[`${entityType}ContactName`].split(' ')
                : '';
              newData['position'] = entity[`${entityType}ContactPosition`];
              newData.contact.mobile = entity[`${entityType}ContactMobile`]
                ? entity[`${entityType}ContactMobile`]
                : '';
              newData.contact.email = entity[`${entityType}ContactEmail`]
                ? entity[`${entityType}ContactEmail`]
                : '';
            }
          } else {
            throw new Error(
              '[ERR] contactDetailsAsEntity Invalid Format: ' + data.contactDetailsAsEntity
            );
          }
        } else {
          nameArr = data.companyContactName ? data.companyContactName.split(' ') : '';
          newData.contact.mobile = data.companyContactMobile ? data.companyContactMobile : '';
          newData.contact.email = data.companyContactEmail ? data.companyContactEmail : '';
          newData['position'] = data.companyContactPosition ? data.companyContactPosition : '';
        }
        break;

      case 'accountant':
        nameArr = data.companyAccountantContactName
          ? data.companyAccountantContactName.split(' ')
          : '';
        newData.contact.mobile = data.companyAccountantPhone ? data.companyAccountantPhone : '';
        newData.contact.email = data.companyAccountantEmail ? data.companyAccountantEmail : '';
        newData['abn'] = data.companyAccountantAbn ? data.companyAccountantAbn : '';
        break;

      default:
        throw new Error('[ERR] Unhandled type: ' + type);
    }

    let i = 0;

    if (nameArr && nameArr.length > 0) {
      nameArr.forEach((n) => {
        console.log('nameArr n', n);

        if (i === 0) {
          newData['firstName'] = n;
        } else if (i === nameArr.length - 1) {
          newData['lastName'] = n;
        } else {
          newData.otherNames.push(n);
        }
        i++;
      });
    }

    console.log('newData', newData);
    console.groupEnd();
    return newData;
  }

  static getCompanyData(type, id, data) {
    const newData = {
      id,
      type: 'company',
    };

    switch (type) {
      case 'accountant':
        newData['abn'] = data.companyAccountantAbn;
        newData['name'] = JSON.parse(data.companyAccountantAbnResult)['match-name']['_text'];
        newData['tradingName'] = '';
        break;

      case 'sole-trader':
        newData['abn'] = data.orgABN;
        newData['name'] = data.orgName;
        newData['tradingName'] = data.orgTrading || '';
        break;

      default:
        throw new Error('[ERR] Unhandled type: ' + type);
    }

    return newData;
  }

  static getValuerName(security) {
    if (!security) {
      return null;
    }

    const { valuers, valuerId } = security;
    if (!valuers) {
      return null;
    }

    const matchedValuer = _.find(valuers, { id: valuerId });
    return matchedValuer ? matchedValuer.name : null;
  }

  parse(origPacket: any, defaults: SubmissionDefaults) {
    const model = new BaseSubmissionModel(defaults);
    this.createPurpose(model, defaults);
    this.createServicing(model);

    // attach data needed for final submission data creation
    if (
      origPacket['step-1'] &&
      origPacket['step-1']['app-company-details'] &&
      origPacket['step-1']['app-personal-details'] &&
      origPacket['step-1']['app-trust-details']
    ) {
      origPacket['step-1']['app-company-details']['trustList'] =
        origPacket['step-1']['app-trust-details'].trustList;
      origPacket['step-1']['app-trust-details']['companyList'] =
        origPacket['step-1']['app-company-details'].companyList;
    }

    const newPacket = this.processWidgets(model, origPacket);
    // add trustees

    // overwrite when wizard is using the old loan details widget
    if (origPacket['step-2'] && origPacket['step-2']['app-loan-details']) {
      newPacket.collaterals[0].category = origPacket['step-2']['app-loan-details']['securityType'];
    }
    newPacket.wizardModel = origPacket;

    return newPacket;
  }

  /**
   * Submissions from submission-api basically have one purpose. Create one to be populated later on
   * @param model
   * @param defaults
   */
  createPurpose(model, defaults: SubmissionDefaults) {
    console.log('createPurpose', model, defaults);

    model.purposes.push({
      id: 1,
      isStaffApplication: false,
      channel: environment.auth.active.source,
      type: defaults.purposeType,
      externalRef: '',
      members: [],
      collaterals: [], // list of collaterals included in the loan
      gstFinanced: false,
      homeOwnerDiscount: false,
      product: {
        name: '',
        rate: 0,
        rtb: 0,
        margin: 0,
        documentationType: '',
        interestType: '',
        fixedTerm: {
          unit: '',
          value: 0,
        },
      },
      purpose: '',
      paidAtSettlement: 0,
      loanAmount: 0,
      depositAmount: 0,
      purchasePrice: 0,
      balloon: 0,
      separateContracts: false,
      term: {
        unit: '',
        value: 0,
      },
      fixed: {
        unit: '',
        value: 0,
      },
      disbursements: [],
      fees: {
        origination: 0,
        application: 0,
        documentation: 0,
        establishment: 0,
        brokerage: 0,
      },
      files: [], // list of files to be added to this product application
    });
    return model;
  }

  // Initialize Servicing
  createServicing(model) {
    model.servicing.push({
      assets: [],
      expenses: [],
      liabilities: [],
      relationships: [],
    });
  }

  // do individual parsing per component
  // chunk up logic in small methods
  private processWidgets(newPacket: BaseSubmissionModel, origPacket) {
    const widgets = {};
    Object.keys(origPacket).forEach((step) => {
      return Object.keys(origPacket[step]).forEach((widget) => {
        widgets[widget] = origPacket[step][widget];
      });
    });

    // if widget is required and missing throw error
    const requiredWidgets = ['app-originator-broker'];

    // so process each widget accordingly
    Object.keys(this.widgetMapper).forEach((widgetName) => {
      if (widgets[widgetName]) {
        newPacket = this.parseWidget(widgetName, widgets[widgetName], newPacket);
      } else if (!widgets[widgetName] && requiredWidgets.includes(widgetName)) {
        throw new Error('Widget is required:' + widgetName);
      }
    });

    return newPacket;
  }

  private parseWidget(widgetName, widgetValue, model: BaseSubmissionModel) {
    if (!this.widgetMapper[widgetName]) {
      throw new Error('Unknown widget ' + widgetName);
    }
    return this.widgetMapper[widgetName](widgetValue, model);
  }

  private mapOriginatorBroker(widgetValue, model: BaseSubmissionModel) {
    Object.keys(widgetValue).forEach((key) => {
      if (widgetValue[key] && widgetValue[key].id) {
        // only use this if a BDM was found
        let id = String(model.parties.length + 1);
        const existingReference = model.parties.find((mp) => mp.value === widgetValue[key].id);
        if (!existingReference) {
          model.parties.push({
            id,
            type: 'reference',
            referenceType: 'partyRoleId',
            value: widgetValue[key].id,
          });
        } else {
          id = existingReference.id;
        }
        model.purposes[0].members.push({
          id,
          role: key,
        });
      }
    });
    return model;
  }

  private mapCustomerDetails(widgetValue, model: BaseSubmissionModel) {
    // No mapping needed
    return model;
  }

  private mapAbnAcn(widgetValue, model: BaseSubmissionModel) {
    return model;
  }

  private mapBorrowerType(widgetValue, model: BaseSubmissionModel) {
    if (!model || !model.parties) {
      return model;
    }
    const { borrowerType } = widgetValue;
    const applicantIds = model.purposes[0].members
      .filter((t) => t.role === 'applicant')
      .map((t) => t.id);

    const trustIndex = model.parties.findIndex(
      (p) => applicantIds.includes(p.id) && p.type === 'company' && p.trust === true
    );
    const companyIndex = model.parties.findIndex(
      (p) => applicantIds.includes(p.id) && p.type === 'company' && !p.trust
    );
    const individuals = model.parties
      .filter((p) => applicantIds.includes(p.id) && p.type === 'individual')
      .map((p) => p.id);
    const nonApplicants = model.parties
      .filter((p) => applicantIds.includes(p.id) && p.type === 'non-applicant')
      .map((p) => p.id);

    if (borrowerType === 'trust-with-individual' && trustIndex >= 0) {
      // if trust with individual, invidivuals become trustees
      model.parties[trustIndex].trustees = individuals;
    } else if (borrowerType === 'trust-with-company' && trustIndex >= 0 && companyIndex >= 0) {
      // if trust with company, company becomes trustees, individuals become directors of company
      model.parties[trustIndex].trustees = [model.parties[companyIndex].id];
      model.parties[companyIndex].directors = individuals;
    } else if (companyIndex >= 0 && trustIndex === -1) {
      // if there is a company but no trust, all individuals are directors of company
      model.parties[companyIndex].directors = individuals;
    }

    return model;
  }

  private mapBorrowerTypeEnhanced(widgetValue, model: BaseSubmissionModel) {
    return model;
  }

  private mapTrustDetails(widgetValue, model: BaseSubmissionModel) {
    widgetValue.trustList.forEach((trust: any) => {
      // add contact person
      let contactId = String(model.parties.length + 1);

      if (!trust.contactSelected) {
        model.parties.push(SubmissionService.getIndividualData('trust', contactId, trust, widgetValue));
      } else {
        const name: any = trust.trustContactName.split(' ');
        const foundContact = model.parties.find((x: any) => x.firstName === name[0] && x.lastName === name[name.length - 1]
          && x.type === 'individual' && x.contact.mobile === trust.trustContactMobile && x.contact.email === trust.trustContactEmail);

        if (foundContact) {
          contactId = foundContact.id;
        } else {
          model.parties.push(SubmissionService.getIndividualData('trust', contactId, trust, widgetValue));
        }
      }

      // add a new person
      const id = String(model.parties.length + 1);

      // handle address setting
      if (trust.postSettlementAddressAsTrading) {
        trust.postSettlementAddress = trust.tradingAddress;
      }
      if (trust.postalAddressAsTrading) {
        trust.postalAddress = trust.tradingAddress;
      }

      let isSMSF = false;
      if (trust.trustType === 'smsf-trust') {
        isSMSF = true;
      }

      const party: any = {
        id,
        partyRoleId: (trust.partyRoleId) ? trust.partyRoleId : null,
        type: 'company',
        name: trust.trustName,
        abn: trust.trustABN,
        acn: trust.trustACN,
        address: {
          trading: SubmissionService.mapAddressDetails(trust.tradingAddress),
          postal: SubmissionService.mapAddressDetails(trust.postalAddress),
          postSettlement: SubmissionService.mapAddressDetails(trust.postSettlementAddress),
        },
        privacyConsent: true, // DEFAULTED TO TRUE for now
        contact: contactId,
        trust: true,
        trustPurposeType: trust.trustType,
        trustees: [],
      };

      if (!party.partyRoleId) {
        delete party.partyRoleId;
      }

      model.parties.push(party);
      model.purposes[0].members.push({
        id,
        guarantor:
          isSMSF === true || // If SMSF-Trust, it will be set as the borrower
            widgetValue.trustList.length === 1 // If number of trusts is 1, it is a Non-SMSF Trust and will be set as the borrower
            ? false
            : true,
        role: 'applicant',
      });
    });

    // remove redundant data used for data setting
    if (widgetValue.companyList) {
      delete widgetValue.companyList;
    }
    return model;
  }

  private mapAddresses(widgetValue) {
    let addressObj = null;
    if (widgetValue) {
      addressObj = {};
    }
    return addressObj;
  }

  private mapCompanyDetails(widgetValue, model: BaseSubmissionModel) {
    widgetValue.companyList.forEach((company: any) => {
      // add contact person
      let contactId = String(model.parties.length + 1);

      if (!company.contactSelected) {
        model.parties.push(SubmissionService.getIndividualData('company', contactId, company, widgetValue));
      } else {
        const name: any = company.companyContactName.split(' ');
        const foundContact = model.parties.find((x: any) => x.firstName === name[0] && x.lastName === name[name.length - 1]
          && x.type === 'individual' && x.contact.mobile === company.companyContactMobile && x.contact.email === company.companyContactEmail);

        if (foundContact) {
          contactId = foundContact.id;
        } else {
          model.parties.push(SubmissionService.getIndividualData('company', contactId, company, widgetValue));
        }
      }

      // add a new person
      const id = String(model.parties.length + 1);

      // handle address setting
      if (company.postSettlementAddressAsTrading) {
        company.postSettlementAddress = company.tradingAddress;
      }
      if (company.postalAddressAsTrading) {
        company.postalAddress = company.tradingAddress;
      }

      const companyData = {
        id,
        partyRoleId: (company.partyRoleId) ? company.partyRoleId : null,
        type: 'company',
        name: company.companyName,
        tradingName: company.tradingName,
        abn: company.companyABN,
        acn: company.companyACN,
        acnRegistered: company.acnRegistered || null,
        address: {
          trading: SubmissionService.mapAddressDetails(company.tradingAddress),
          postal: SubmissionService.mapAddressDetails(company.postalAddress),
          postSettlement: SubmissionService.mapAddressDetails(company.postSettlementAddress),
        },
        privacyConsent: true, // DEFAULTED TO TRUE for now
        contact: contactId,
        trust: false,
        industry: company.industryType,
        companyType: DEFAULTS.COMPANY_TYPE,
        companyClassification: DEFAULTS.COMPANY_CLASSIFICATION,
      };

      if (!companyData.partyRoleId) {
        delete companyData.partyRoleId;
      }

      let isSMSF = false;
      if (company.trusteeOf >= 0 && company.trusteeOf !== null) {
        isSMSF = true;

        console.log('model.parties', model.parties);

        const trusts = model.parties.filter((p: any) => p.type === 'company' && p.trust === true);
        const destinationParty = _.get(trusts, company.trusteeOf, {});
        destinationParty.trustees = destinationParty.trustees || [];
        destinationParty.trustees.push(id);
      }

      // add accountant
      if (company.companyHasAccountant) {
        // create accountant individual
        const accountantId = String(model.parties.length + 2);
        model.parties.push(SubmissionService.getIndividualData('accountant', accountantId, company));

        // create accountant company
        const accountantCompanyId = String(model.parties.length + 2);
        const accountantCompanyData = SubmissionService.getCompanyData('accountant', accountantCompanyId, company);
        accountantCompanyData['employee'] = accountantId;
        model.parties.push(accountantCompanyData);

        companyData['accountant'] = accountantCompanyId;
      }

      model.parties.push(companyData);
      model.purposes[0].members.push({
        id,
        guarantor: isSMSF === true ? true : false, // If SMSF, all companies should be set to guarantor
        role: 'applicant',
      });
    });

    // remove redundant data used for data setting
    if (widgetValue.trustList) {
      delete widgetValue.trustList;
    }
    return model;
  }

  private mapPhones(phoneType, phoneArray) {
    phoneArray = phoneArray || [];
    return _.compact(
      phoneArray
        .filter((phone) => phone.phoneType === phoneType)
        .map((phone) => {
          if (phone && phone.phoneNumber) {
            return phone.phoneNumber.trim();
          } else {
            return null;
          }
        })
    );
  }

  private mapPersonalDetails(widgetValue, model: BaseSubmissionModel) {
    const borrowersCount = widgetValue.personList.length;
    const applicantIds = [];
    widgetValue.personList.forEach((p) => {
      console.log('mapPersonalDetails', p);

      if (p.firstName && p.lastName) {
        // add a new person
        const id = String(model.parties.length + 1);
        applicantIds.push(id);
        const address = {
          residential: SubmissionService.mapAddressDetails(
            p.homeAddress,
            p.stayAtHomeYears * 12 + p.stayAtHomeMonths,
            p.residencyType
          ),
          postal: SubmissionService.mapAddressDetails(p.postalAddress),
          postSettlement: SubmissionService.mapAddressDetails(p.postSettlementAddress),
        };
        // add previous address
        if (p.previousAddresses && p.previousAddresses.length > 0) {
          address['previous'] = p.previousAddresses.map((prevAddr) => {
            return SubmissionService.mapAddressDetails(
              prevAddr.previousAddress,
              prevAddr.stayAtPrevYears * 12 + prevAddr.stayAtPrevMonths,
              prevAddr.prevResidencyType
            );
          });
        }

        let roleType = 'applicant';
        if (p.nonApplicant) {
          roleType = 'nonApplicant';
        }

        const party: any = {
          id,
          partyRoleId: (p.partyRoleId) ? p.partyRoleId : null,
          type: 'individual',
          firstName: _.trim(p.firstName),
          otherNames: [_.trim(p.middleName)],
          lastName: _.trim(p.lastName),
          contact: {
            fixed: this.mapPhones('fixed', p.phones),
            email: _.trim(p.email),
            mobile: this.mapPhones('mobile', p.phones),
            fax: this.mapPhones('fax', p.phones),
          },
          dependentsYoung: p.dependentsUnder11 || 0,
          dependents: p.dependentsOver11 || 0,
          dateOfBirth: p.dateOfBirth,
          driversLicence: _.trim(p.driversLicence),
          driversLicenceState: p.driversLicenceState,
          driversLicenceCardNumber: _.trim(p.driversLicenceCardNumber),
          auCitizen: !p.auCitizen,
          maritalStatus: p.maritalStatus,
          electronicIdentity: !p.optOutElectronicIdentityAcceptance,
          electronicDelivery: !p.optOutElectronicDeliveryAcceptance,
          privacyConsent: p.privacyConsent,
          address,
          isNccpQuestionCovered: p.nccpCovered,
          nccpNotes: p.nccpNotes,
          creditReportEnquiryId: p.creditReportEnquiryId ? p.creditReportEnquiryId : null,
        };

        if (!party.partyRoleId) {
          delete party.partyRoleId;
        }

        model.parties.push(party);
        model.purposes[0].members.push({
          id,
          guarantor: p.borrowerGuarantor && p.borrowerGuarantor === 'guarantor' ? true : false,
          role: roleType,
        });

        // add relationship, if owns a property (business)
        if (p.ownProperty) {
          if (p.maritalStatus === 'married' || p.maritalStatus === 'defacto') {
            model.servicing[0].relationships.push({
              from: id,
              to: null,
              type: 'couple-with-non-applicant',
            });
          } else {
            model.servicing[0].relationships.push({
              from: id,
              to: null,
              type: 'single',
            });
          }
        }

        // set the lead's post code if it is not yet set
        if (!model.lead.postcode && p.homeAddress) {
          model.lead.postcode = p.homeAddress.postcode;
        }

        // add company for sole-trader
        if (p.showPersonABN) {
          if (borrowersCount === 1) {
            const soleCompanyId = String(model.parties.length + 1);
            const soleCompanyData = SubmissionService.getCompanyData('sole-trader', soleCompanyId, p);
            soleCompanyData['soleTrader'] = id;
            model.parties.push(soleCompanyData);
          } else if (borrowersCount > 1) {
            const companyIndex = model.parties.findIndex(
              (x) => x.type === 'company' && x['soleTrader']
            );
            if (companyIndex < 0) {
              const soleCompanyId = String(model.parties.length + 1);
              const soleCompanyData = SubmissionService.getCompanyData(
                'sole-trader',
                soleCompanyId,
                p
              );
              soleCompanyData['soleTrader'] = [id];
              model.parties.push(soleCompanyData);
            } else {
              model.parties[companyIndex]['soleTrader'].push(id);
            }
          }
        }
      }
    });

    // Only handle relationships when all applicants have been added
    widgetValue.personList.forEach((p, idx) => {
      // add relationships in servicing
      const id = applicantIds[idx];
      if (p.relationships && p.relationships.length > 0) {
        if (borrowersCount === 1) {
          model.servicing[0].relationships.push({
            from: id,
            to: null,
            type: p.relationships[0].type,
          });
        } else {
          p.relationships.forEach((rel) => {
            const toIndex = rel.to - 1;

            // need to make sure there are no duplicates of from -- to
            const from = id;
            const to = applicantIds[toIndex];
            const duplicate = model.servicing[0].relationships.find(
              (r) => (r.to === from && r.from === to) || (r.from === from && r.to === to)
            );
            if (!duplicate) {
              model.servicing[0].relationships.push({
                from,
                to,
                type: rel.type,
              });
            }
          });
        }
      }

      if (p.companyRelationships && p.companyRelationships.length > 0) {
        p.companyRelationships.forEach((rel) => {
          const trusts = model.parties.filter((x: any) => x.type === 'company' && x.trust);
          const companies = model.parties.filter((x: any) => x.type === 'company' && !x.trust);
          const [destination, index] = _.split(rel.to, '-');
          const destinationParty =
            destination === 'trust' ? _.get(trusts, index) : _.get(companies, index);
          if (rel.type === 'director') {
            destinationParty.directors = destinationParty.directors || [];
            destinationParty.directors.push(id);
          } else if (rel.type === 'trustee') {
            destinationParty.trustees = destinationParty.trustees || [];
            destinationParty.trustees.push(id);
          }
        });
      }
    });
    return model;
  }

  private mapLoanDetails(widgetValue, model: BaseSubmissionModel) {
    console.log('mapLoanDetails', widgetValue);

    model.purposes[0].purpose = widgetValue.purpose;
    model.purposes[0].product.name = widgetValue.product;
    model.purposes[0].product.pricing = widgetValue.productPricing;
    model.purposes[0].product.monthlyFee = widgetValue.monthlyFee;
    model.purposes[0].product.allowCards = widgetValue.allowCards;
    model.purposes[0].product.interestType = widgetValue.interestType;
    model.purposes[0].product.fixedTerm =
      widgetValue.interestType === 'fixed'
        ? {
          unit: widgetValue.loanTerm.rate.term.unit,
          value: +(widgetValue.loanTerm.rate.term.value),
        }
        : null;
    return model;
  }

  private mapSingleSplitLoanAmount(widgetValue, model: BaseSubmissionModel) {
    console.log('widgetValue mapSingleSplitLoanAmount', widgetValue);

    model.purposes[0].purchasePrice = widgetValue.purchaseRefinanceAmount;
    model.purposes[0].paidAtSettlement = widgetValue.amountPaid;
    model.purposes[0].depositAmount = widgetValue.depositTradeAmount;
    model.purposes[0].loanAmount = widgetValue.totalLoanAmount;
    model.purposes[0].homeOwnerDiscount = widgetValue.homeOwnerDiscount;
    model.purposes[0].fees = [];
    model.purposes[0].fees.push({
      type: 'brokerage',
      description: widgetValue.brokerageFeeCapitalizedCheck ? 'funded' : 'paid-by-funder', // brokerage to be dependent if brokerage fee is capitalized or not
      amount: widgetValue.brokerageFee ? widgetValue.brokerageFee : 0,
    });
    model.purposes[0].fees.push({
      type: 'origination',
      description: null,
      amount: widgetValue.originationFee ? widgetValue.originationFee : 0,
    });
    model.purposes[0].fees.push({
      type: 'application',
      description: null,
      amount: widgetValue.applicationFee,
    });

    // iterate through insurances
    widgetValue.insurances.forEach((insurance) => {
      if (insurance.insuranceId) {
        model.purposes[0].disbursements.push({
          type: insurance.insuranceId,
          description: '',
          amount: insurance.insuranceAmount,
        });
      }
    });
    return model;
  }

  private mapBalloonAmount(widgetValue, model: BaseSubmissionModel) {
    model.purposes[0].balloon = widgetValue.amount;
    return model;
  }

  private mapLoanTerms(widgetValue, model: BaseSubmissionModel) {
    console.log('mapLoanTerms', widgetValue, model);

    const rate =
      Number(_.get(widgetValue, 'loanTerm.rate.rtb', 0)) * 100 +
      Number(_.get(widgetValue, 'loanTerm.rate.loading', 0)) * 100;
    model.purposes[0].term.frequency = widgetValue.repaymentFrequency;
    model.purposes[0].term.value = Number(_.get(widgetValue, 'loanTerm.rate.term.value'));
    model.purposes[0].term.unit = _.get(widgetValue, 'loanTerm.rate.term.unit');
    model.purposes[0].homeOwnerDiscount = widgetValue.homeOwnerDiscount;

    console.log('model.purposes[0].product', model.purposes[0]);

    if (widgetValue.interestType === 'fixed') {
      model.purposes[0].product.fixedTerm.value = Number(
        _.get(widgetValue, 'loanTerm.rate.term.value')
      );
      model.purposes[0].product.fixedTerm.unit = _.get(widgetValue, 'loanTerm.rate.term.unit');
    }

    model.purposes[0].product.margin = Number(_.get(widgetValue, 'loanTerm.rate.margin'));
    model.purposes[0].product.rtb = rate / 100;
    model.purposes[0].product.rate = widgetValue.interestRate;

    return model;
  }

  private mapAutoCollateral(widgetValue, model: BaseSubmissionModel) {
    const id = String(model.collaterals.length + 1);
    model.collaterals.push({
      id,
      description: widgetValue.vehicle.description,
      type: 'motor-vehicle-loan',
      make: (widgetValue.vehicle.make && widgetValue.vehicle.make.toLowerCase() !== 'tba') ? widgetValue.vehicle.make : null,
      model: (widgetValue.vehicle.model && widgetValue.vehicle.model.toLowerCase() !== 'tba') ? widgetValue.vehicle.model : null,
      year: widgetValue.vehicle.year,
      nvic: (widgetValue.variant !== 'my-variant-is-not-listed') ? widgetValue.variant : null,
      variant: (widgetValue.variant === 'my-variant-is-not-listed') ? widgetValue.variant : null,
      className: widgetValue.specs ? widgetValue.specs.className : null,
      odometerReading: (widgetValue.odometerReading) ? +(widgetValue.odometerReading) : null,
      gvm: widgetValue.specs ? widgetValue.specs.gvm : null,
      co2: widgetValue.specs ? widgetValue.specs.co2Emission : null,
      usage: widgetValue.usage,
      vin: widgetValue.vin,
      class: widgetValue.vehicleType,
      category: widgetValue.securityType ? widgetValue.securityType : 'new',
    });
    model.purposes[0].collaterals.push(id);

    model.lead.relatesTo = widgetValue.vehicleType + '-loan';

    return model;
  }

  private mapSubmit(widgetValue, model: BaseSubmissionModel) {
    return model;
  }

  private mapDocsToUpload(widgetValue, model: BaseSubmissionModel) {
    if (widgetValue.files) {
      console.log('mapDocsToUpload', widgetValue.files);

      const trust = model.parties.filter((p) => p.type === 'company' && p.trust === true);
      const company = model.parties.filter((p) => p.type === 'company' && !p.trust);
      const companyAndTrustContactIds = [...company, ...trust].map((item) => item.contact);

      const parties = {
        trust,
        company,
        // remove company and trust contacts from person mapping
        person: model.parties.filter(
          (p) => p.type === 'individual' && !companyAndTrustContactIds.includes(p.id))
      };

      widgetValue.files.forEach((element, index) => {
        console.log('mapDocsToUpload', element);

        const purposeFileData: any = {
          id: index + 1,
          key: element.key,
          party: null,
          collateral: null
        };

        const fileData: any = {
          id: index + 1,
          key: element.key,
          documentTypeCode: element.documentTypeCode,
          name: element.origName,
          comments: element.comments,
        };

        if (element.party) {
          const [destination, index] = _.split(element.party, '-');
          const party = _.get(parties, `${destination}.${index}`);
          purposeFileData.party = (party && party.id) ? party.id : null;
        } else if (element.security) {
          const index: any = _.split(element.security, '-').pop();
          const collateral = model.collaterals[index];

          purposeFileData.collateral = (collateral && collateral.id) ? collateral.id : null;
        }

        if (!purposeFileData.collateral) {
          delete purposeFileData.collateral;
        }

        if (!purposeFileData.party) {
          delete purposeFileData.party;
        }

        model.purposes[0].files.push(purposeFileData);

        if (!_.find(model.files, { key: fileData.key })) {
          model.files.push(fileData);
        }
      });
    }
    return model;
  }

  private mapEmployments(widgetValue, model: BaseSubmissionModel) {
    let employmentId = 1;
    widgetValue.employmentList.forEach((employment: any) => {
      const applicantIndex = model.parties.findIndex(
        (a) => a.type === 'individual' && a.address && !a.income
      );

      model.parties[applicantIndex]['employments'] = [];
      model.parties[applicantIndex]['income'] = {};

      if (employment.otherIncomes && employment.otherIncomes.length > 0) {
        employment.otherIncomes.forEach((income: any) => {
          if (!model.parties[applicantIndex]['income'][income.type]) {
            model.parties[applicantIndex]['income'][income.type] = [];
          }
          model.parties[applicantIndex]['income'][income.type].push({
            amount: +income.amount,
            frequency: income.frequency,
          });
        });
      }

      if (employment.nonTaxableIncomes && employment.nonTaxableIncomes.length > 0) {
        employment.nonTaxableIncomes.forEach((income: any) => {
          if (!model.parties[applicantIndex]['income'][income.type]) {
            model.parties[applicantIndex]['income'][income.type] = [];
          }
          model.parties[applicantIndex]['income'][income.type].push({
            amount: +income.amount,
            frequency: income.frequency,
          });
        });
      }

      if (employment.currentEmployments && employment.currentEmployments.length > 0) {
        employment.currentEmployments.forEach((currentEmployment: any) => {
          const total_duration =
            currentEmployment.lengthYears * 12 + currentEmployment.lengthMonths;
          const employmentModel: any = {
            currency: 'current',
            categoryType: currentEmployment.category,
            employer: (currentEmployment.employer) ? currentEmployment.employer : '',
            occupation: currentEmployment.occupation,
            basis: currentEmployment.basis,
            status: currentEmployment.status,
            businessType: currentEmployment.businessType,
            probation: currentEmployment.probation,
            anzsicCategory: currentEmployment.anzsicCategory,
            duration: {
              value: total_duration,
              unit: 'months',
            },
            id: employmentId,
          };

          if (
            currentEmployment.status === 'self-employed' &&
            currentEmployment.basis === 'self-employed'
          ) {
            const link = currentEmployment.link ? currentEmployment.link : uuid.v1();
            employmentModel.link = link;
            employmentModel.abn = currentEmployment.abnAcnNumber;

            const factor = new Decimal(currentEmployment.percentage || 100).div(100);
            const ownership = factor.absoluteValue().toNumber();
            if (!model.parties[applicantIndex]['income']['income-company-profit-before-tax']) {
              model.parties[applicantIndex]['income']['income-company-profit-before-tax'] = [];
            }
            model.parties[applicantIndex]['income']['income-company-profit-before-tax'] =
              model.parties[applicantIndex]['income']['income-company-profit-before-tax'].concat([
                {
                  amount: (currentEmployment.currentYearIncome) ? Number(
                    factor.mul(new Decimal(currentEmployment.currentYearIncome)).toFixed(4)) : 0,
                  employmentId: employmentId,
                  frequency: 'annually',
                  currency: 'current',
                  ownership,
                  link: link,
                },
                {
                  amount: (currentEmployment.previousYearIncome) ? Number(
                    factor.mul(new Decimal(currentEmployment.previousYearIncome)).toFixed(4)) : 0,
                  employmentId: employmentId,
                  frequency: 'annually',
                  currency: 'previous',
                  ownership,
                  link: link,
                },
              ]);

            let addbacks = currentEmployment.allowableAddBacks;
            if (!currentEmployment.parent) {
              // if this is not a parent, get the parent's addback
              // get the addbacks from the parent employment

              const theParent = _.chain(widgetValue.employmentList)
                .map('currentEmployments')
                .flatten()
                .find((emp) => emp.link === link && emp.parent)
                .value();

              addbacks = _.get(theParent, 'allowableAddBacks', []);
            }

            addbacks.forEach((addback: any) => {
              const addbackLink = addback.addbackLink;
              if (!model.parties[applicantIndex]['income'][addback.addbackType]) {
                model.parties[applicantIndex]['income'][addback.addbackType] = [];
              }
              model.parties[applicantIndex]['income'][addback.addbackType] = model.parties[
                applicantIndex
              ]['income'][addback.addbackType].concat([
                {
                  amount: Number(
                    factor.mul(new Decimal(addback.addbackCurrentYearIncome)).toFixed(4)
                  ),
                  description: addback.addbackDescription,
                  employmentId: employmentId,
                  frequency: 'annually',
                  currency: 'current',
                  ownership,
                  link: addbackLink,
                },
                {
                  amount: Number(
                    factor.mul(new Decimal(addback.addbackPreviousYearIncome)).toFixed(4)
                  ),
                  description: addback.addbackDescription,
                  employmentId: employmentId,
                  frequency: 'annually',
                  currency: 'previous',
                  ownership,
                  link: addbackLink,
                },
              ]);
            });
          } else {
            currentEmployment.incomes.forEach((income: any) => {
              if (!model.parties[applicantIndex]['income'][income.type]) {
                model.parties[applicantIndex]['income'][income.type] = [];
              }
              model.parties[applicantIndex]['income'][income.type].push({
                amount: +income.amount,
                frequency: income.frequency,
                employmentId: employmentId,
              });
            });
          }

          model.parties[applicantIndex]['employments'].push(employmentModel);
          employmentId++;
        });
      }

      if (employment.previousEmployments && employment.previousEmployments.length > 0) {
        employment.previousEmployments.forEach((previousEmployments: any) => {
          const total_duration =
            previousEmployments.lengthYears * 12 + previousEmployments.lengthMonths;
          const employmentModel = {
            currency: 'previous',
            categoryType: previousEmployments.category,
            employer: (previousEmployments.employer) ? previousEmployments.employer : '',
            occupation: previousEmployments.occupation,
            basis: previousEmployments.basis,
            status: previousEmployments.status,
            businessType: previousEmployments.businessType,
            probation: previousEmployments.probation,
            anzsicCategory: previousEmployments.anzsicCategory,
            duration: {
              value: total_duration,
              unit: 'months',
            },
            id: employmentId,
          };

          model.parties[applicantIndex]['employments'].push(employmentModel);
          employmentId++;
        });
      }
    });

    return model;
  }

  private mapCompanyIncomes(widgetValue, model: BaseSubmissionModel) {
    const allTrusts = _.filter(model.parties, (p) => p.type === 'company' && p.trust);
    const allCompanies = _.filter(model.parties, (p) => p.type === 'company' && !p.trust);

    const mapToModelParty = (source, destination) => {
      _.each(source, (income, idx) => {
        const modelParty = _.get(destination, idx);
        if (modelParty) {
          const partyIndex = _.findIndex(model.parties, (p) => p.id === modelParty.id);

          // process other Incomes
          if (!modelParty['income']) {
            modelParty.income = {};
          }
          _.each(income.otherIncomes, (otherIncome) => {
            if (!modelParty['income'][otherIncome.type]) {
              modelParty['income'][otherIncome.type] = [];
            }
            modelParty['income'][otherIncome.type].push({
              amount: +otherIncome.amount,
              frequency: otherIncome.frequency,
            });
          });

          // process business income
          const link = income.link ? income.link : uuid.v1();
          income.link = link;

          if (!model.parties[partyIndex]['income']['income-company-profit-before-tax']) {
            model.parties[partyIndex]['income']['income-company-profit-before-tax'] = [];
          }
          model.parties[partyIndex]['income']['income-company-profit-before-tax'] = model.parties[
            partyIndex
          ]['income']['income-company-profit-before-tax'].concat([
            {
              amount: Number(income.currentYearIncome ? income.currentYearIncome : 0).toFixed(4),
              frequency: 'annually',
              currency: 'current',
              ownership: 1,
              link: link,
            },
            {
              amount: Number(income.previousYearIncome ? income.previousYearIncome : 0).toFixed(4),
              frequency: 'annually',
              currency: 'previous',
              ownership: 1,
              link: link,
            },
          ]);

          const addbacks = income.allowableAddBacks ? income.allowableAddBacks : [];
          addbacks.forEach((addback: any) => {
            const addbackLink = addback.addbackLink;
            if (!model.parties[partyIndex]['income'][addback.addbackType]) {
              model.parties[partyIndex]['income'][addback.addbackType] = [];
            }
            model.parties[partyIndex]['income'][addback.addbackType] = model.parties[partyIndex][
              'income'
            ][addback.addbackType].concat([
              {
                amount: Number(addback.addbackCurrentYearIncome).toFixed(4),
                description: addback.addbackDescription,
                frequency: 'annually',
                currency: 'current',
                ownership: 1,
                link: addbackLink,
              },
              {
                amount: Number(addback.addbackPreviousYearIncome).toFixed(4),
                description: addback.addbackDescription,
                frequency: 'annually',
                currency: 'previous',
                ownership: 1,
                link: addbackLink,
              },
            ]);
          });
        }
      });
    };

    mapToModelParty(widgetValue.trustIncomeList, allTrusts);
    mapToModelParty(widgetValue.companyIncomeList, allCompanies);

    return model;
  }

  private mapAssets(widgetValue, model: BaseSubmissionModel) {
    const assets = model.servicing[0].assets;

    console.log('model.parties assets', model.parties);

    const parties = {
      trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
      company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
      person:
        model.parties.filter(
          (p) =>
            p.type === 'individual' &&
            _.find(model.purposes[0].members, (m) => m.id === p.id)
        ) || [],
    };

    console.log('assets parties', parties);
    console.log('assets model.purposes[0].members', model.purposes[0].members);

    _.each(widgetValue.assetsList, (asset) => {
      const ALEownership = [];

      const details = {
        ...asset,
        amount: Number(asset.amount),
        refinancing: !!asset.refinance,
      };

      if (asset.parties && asset.parties.length) {
        const partiesWithOwnerships = asset.parties.filter((p) => Number(p.ownership) > 0);
        _.each(partiesWithOwnerships, (party) => {
          // add to each party - but it should
          let ownership = 1;
          const p = _.get(parties, `${party.partyType}.${party.index}`);
          ownership = party.ownership;
          const factor = new Decimal(ownership).div(100);
          if (p) {
            ALEownership.push({
              id: p.id,
              value: factor,
            });
          }
        });
      }

      details.ownership = ALEownership;
      delete details.parties;
      assets.push(details);
    });

    model.servicing[0].assets = assets;
    return model;
  }

  private mapLiabilities(widgetValue, model: BaseSubmissionModel) {
    const liabilities = model.servicing[0].liabilities;

    const parties = {
      trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
      company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
      person:
        model.parties.filter(
          (p) =>
            p.type === 'individual' &&
            _.find(model.purposes[0].members, (m) => m.id === p.id)
        ) || [],
    };

    _.each(widgetValue.liabilitiesList, (liability) => {
      const ALEownership = [];
      const details = {
        type: liability.type,
        amount: Number(liability.amount),
        limit: Number(liability.limitValue),
        description: liability.description,
        lender: _.get(liability, 'liability.lender.partyRoleId', liability.lender),
        refinancing: liability.refinance,
        interestOnly: liability.interestOnly,
        postSettlement: liability.postSettlement,
        ownership: null,
      };

      if (liability.parties && liability.parties.length) {
        const partiesWithOwnerships = liability.parties.filter((p) => Number(p.ownership) > 0);
        _.each(partiesWithOwnerships, (party) => {
          // add to each party - but it should
          let ownership = 1;
          const p = _.get(parties, `${party.partyType}.${party.index}`);
          ownership = party.ownership;
          const factor = new Decimal(ownership).div(100);
          if (p) {
            ALEownership.push({
              id: p.id,
              value: factor.toNumber(),
            });
          }
        });
        details.ownership = ALEownership;
      }
      liabilities.push(details);
    });

    model.servicing[0].liabilities = liabilities;
    return model;
  }

  private mapExpenses(widgetValue, model: BaseSubmissionModel) {
    const parties = {
      trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
      company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
      person:
        model.parties.filter(
          (p) =>
            p.type === 'individual' &&
            _.find(model.purposes[0].members, (m) => m.role === 'applicant' && m.id === p.id)
        ) || [],
    };

    model.servicing[0].expenses = widgetValue.expensesList.map((e) => {
      e.amount = Number(e.amount);
      const ALEownership = [];
      const ownership = Number(new Decimal(100).div(parties.person.length).toFixed(4));
      _.each(parties.person, (p) => {
        const factor = new Decimal(ownership).div(100);
        ALEownership.push({
          id: p.id,
          value: factor.toNumber(),
        });
      });
      e.ownership = ALEownership;
      return e;
    });
    return model;
  }

  private mapTermDetails(widgetValue, model: BaseSubmissionModel) {
    model = this.mapLoanTerms(widgetValue, model);
    model = this.mapLoanDetails(widgetValue, model);
    model = this.mapAutoCollateral(widgetValue, model);
    model.purposes[0].balloon = widgetValue.balloonAmount;
    return model;
  }

  private mapQuoteDetails(widgetValue, model: BaseSubmissionModel) {
    model = this.mapLoanTerms(widgetValue, model);
    model = this.mapLoanDetails(widgetValue, model);
    model = this.mapAutoCollateral(widgetValue, model);
    model = this.mapSingleSplitLoanAmount(widgetValue, model);
    model.purposes[0].balloon = widgetValue.balloonAmount;
    return model;
  }

  private mapQuoteResults(widgetValue, model: BaseSubmissionModel) {
    return model;
  }

  private mapApplicationType(widgetValue, model: BaseSubmissionModel) {
    console.log('mapApplicationType', widgetValue);

    model.purposes[0].applicationType = widgetValue.vxApplicationType;

    if (widgetValue.isStaffApplication) {
      model.purposes[0].isStaffApplication = widgetValue.isStaffApplication;
    }

    if (widgetValue.appId && widgetValue.furtherAdvanceApplicationType) {
      if (widgetValue.furtherAdvanceApplicationType === 'existing-security') {
        model.purposes[0].parentApplication = widgetValue.appId;
      }

      if (widgetValue.furtherAdvanceApplicationType !== 'existing-security') {
        model.purposes[0].applicationType = null;
      }
    }

    if (widgetValue.applicationType === 'new-bridging') {
      model.lead.relatesTo = 'bridging-loan';
    }

    if (widgetValue.accountNumber) {
      _.get(model, 'purposes[0].products[0]', {}).parentSettlementXref = widgetValue.accountNumber;
    }

    return model;
  }

  private mapHomeLoanDetails(widgetValue, model: BaseSubmissionModel) {
    console.log('mapHomeLoanDetails', JSON.stringify(widgetValue, null, 2));

    model.purposes[0].purpose = widgetValue.primaryPurpose;
    model.purposes[0].skipAutomaticDisbursementCreation = true;

    if (widgetValue.mortgageInsurance) {
      model.purposes[0].mortgageInsurance = {
        policy: widgetValue.mortgageInsurance.policy,
        paidByFunder: widgetValue.mortgageInsurance.funderPays,
        insurerPartyRoleId : widgetValue.mortgageInsurance.insurer,
        premium: widgetValue.mortgageInsurance.premium,
        expiry: widgetValue.mortgageInsurance.expiry,
        gst: widgetValue.mortgageInsurance.gst,
        overrideMortgageInsurer : null, // there is no value for this on the packet retrieve
        preferCapitalised : widgetValue.mortgageInsurance.capitalise,
        stampDuty : widgetValue.mortgageInsurance.stampDuty
      };
    }

    widgetValue.purposes.forEach((p, i) => {
      if (p.address && p.address.flatSearch === 'TBA') {
        p.description = 'TBA';
        p.address = null;
      }

      model.purposes[0].disbursements.push({
        type: p.purpose,
        description: p.description,
        lender: p.creditProvider,
        securityAddress: SubmissionService.mapAddressDetails(p.address),
        details: p.details,
        amount: Number(p.amount),
      });
    });

    model.purposes[0].deposits = [];
    widgetValue.deposits.forEach((d) => {
      if (d.depositType) {
        model.purposes[0].deposits.push({
          type: d.depositType,
          description: d.depositDescription,
          amount: Number(d.depositAmount),
        });
      }
    });

    model.purposes[0].fees = [];
    widgetValue.fees.forEach((f) => {
      if (f.feeType) {
        model.purposes[0].fees.push({
          type: f.feeType,
          description: f.feeDescription,
          amount: Number(f.feeAmount),
        });
      }
    });

    model.purposes[0].products = [];
    widgetValue.splits.forEach((s) => {
      const rate = new Decimal(s.rate || 0).div(100).toNumber();
      const margin = new Decimal(s.margin || 0).div(100).toNumber();
      const adjustment = new Decimal(s.adjustment || 0).div(100).toNumber();

      const interestOnlyTerm = s.interestOnlyTerm
        ? {
          unit: s.interestOnlyTerm.split('-')[1],
          value: +(s.interestOnlyTerm.split('-')[0]),
        }
        : null;
      const fixedRateTerm = s.fixedRateTerm
        ? {
          unit: s.fixedRateTerm.split('-')[1],
          value: +(s.fixedRateTerm.split('-')[0]),
        }
        : null;
      const honeymoon = s.honeymoon
        ? {
          unit: s.honeymoon.split('-')[1],
          value: +(s.honeymoon.split('-')[0]),
        }
        : null;

      model.purposes[0].products.push({
        product: {
          name: s.product,
          pricing: s.productPricing,
          displayName: s.displayName,
          isShariaCompliant: s.isShariaCompliant,
          isSMSF: s.isSMSF,
          isGreen: s.isGreen,
          isReferenceRateDisclosed: s.isReferenceRateDisclosed,
          referenceRate: s.referenceRate,
          honeymoon,
          allowCards: s.allowCards,
          interestType: s.interestType,
          rate: s.interestRate,
          rtb: rate, // RTB in submission-api is the original rate -- split.rate will be calculated eventually with the formula (mdr + margin) + adjustment
          margin, // margin is the margin value
          adjustment, // adjustment
          adjustmentType: s.adjustmentType,
          adjustmentExpiry: s.adjustmentExpiry,
          documentationType: '',
          fixedTerm: s.fixedRate,
        },
        amount: Number(s.splitAmount),
        repayment: s.repayment,
        term: s.term
          ? {
            unit: s.term.split('-')[1],
            frequency: 'monthly',
            value: +(s.term.split('-')[0]),
          }
          : null,
        interestOnlyTerm,
        fixedRateTerm,
      });
    });
    model.purposes[0].sacrifice = widgetValue.sacrifice;
    model.purposes[0].loanAmount = Number(widgetValue.loanFacilityTotal);

    delete model.purposes[0].term;
    delete model.purposes[0].product;

    return model;
  }

  private mapRateComponent(widgetValue, model: BaseSubmissionModel) {
    return model;
  }

  private mapSecurityDetails(widgetValue: any, model: BaseSubmissionModel) {
    widgetValue.securityList.forEach((security: any) => {
      console.log('mapSecurityDetails', security);

      const id = String(model.collaterals.length + 1);
      const collateral: any = {
        id,
        collateralId: security.collateralId,
        type: 'home-loan',
        address: SubmissionService.mapAddressDetails(security.securityAddress),
        dates: {
          contract: security.contractDate,
          finance: security.financeDate,
          settlement: security.settlementDate,
        },
        occupancyType: security.occupancy,
        propertyType: security.securityType,
        mortgageType: security.mortgageType,
        propertyStatusType: security.securityStatus,
        estimatedValue: parseFloat(security.estimatedValue),
        isBeingPurchased: security.propertyBeingPurchased,
        valuation: {
          valexId: security.valexId,
          type: security.valuationType,
          orderValuer: security.valuationType === 'new-valuation' ? (security.valuerName ? security.valuerName : SubmissionService.getValuerName(security)) : null,
          orderValuerId: security.valuationType === 'new-valuation' ? security.valuerId : null,
          propertyAccessInstruction: security.accessInstructions,
          valuationContactName: security.valuationContactName,
          valuationContactNumber: security.valuationContactNumber,
          instructions: {
            comments: security.valuerAccessInstructions,
            contacts: []
          }
        },
        members: [],
      };

      // populate valuer access contact details
      if (security.valuerAccessBorrower) {
        const parties = {
          trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
          company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
          person: model.parties.filter((p) => p.type === 'individual') || [],
        };

        security.valuerAccessBorrowers.forEach((borrower) => {
          if (borrower.selected) {
            const foundPersonRecord: any = parties.person.find(x => x.firstName === borrower.firstName && x.lastName === borrower.lastName && x.dateOfBirth === borrower.dateOfBirth);
            if (foundPersonRecord) {
              collateral.valuation.instructions.contacts.push({
                id: foundPersonRecord.id,
                role: 'requestor'
              });
            }
          }
        });
      } else if (security.valuerAccessOther) {
        const partyId = model.parties.length + 1;
        const valuerAccessOtherParty: any = {
          id: String(model.parties.length + 1),
          type: 'individual',
          firstName: security.valuerAccessOtherContactName.split(' ')[0],
          lastName: (security.valuerAccessOtherContactName.split(' ')[1]) ? security.valuerAccessOtherContactName.split(' ')[1] : '',
          contact: {
            email: security.valuerAccessOtherEmail,
            mobile: [],
            fixed: [],
          },
        };

        if (security.valuerAccessOtherPhoneOneType === 'mobile' && security.valuerAccessOtherPhoneOneNumber) {
          valuerAccessOtherParty.contact.mobile.push(security.valuerAccessOtherPhoneOneNumber);
        } if (security.valuerAccessOtherPhoneOneType !== 'mobile' && security.valuerAccessOtherPhoneOneNumber) {
          valuerAccessOtherParty.contact.fixed.push(security.valuerAccessOtherPhoneOneNumber);
        }

        if (security.valuerAccessOtherPhoneTwoType === 'mobile' && security.valuerAccessOtherPhoneTwoNumber) {
          valuerAccessOtherParty.contact.mobile.push(security.valuerAccessOtherPhoneTwoNumber);
        } if (security.valuerAccessOtherPhoneTwoType !== 'mobile' && security.valuerAccessOtherPhoneTwoNumber) {
          valuerAccessOtherParty.contact.fixed.push(security.valuerAccessOtherPhoneTwoNumber);
        }

        if (valuerAccessOtherParty.contact.fixed.length === 0) {
          delete valuerAccessOtherParty.contact.fixed;
        }

        if (valuerAccessOtherParty.contact.mobile.length === 0) {
          delete valuerAccessOtherParty.contact.mobile;
        }

        model.parties.push(valuerAccessOtherParty);

        if (!security.valuerAccessOtherCompanyName) {
          collateral.valuation.instructions.contacts.push({ id: String(model.parties.length), role: security.valuerAccessOtherContactType });
        } else if (security.valuerAccessOtherCompanyName) {
          collateral.valuation.instructions.contacts.push({ id: String(model.parties.length + 1), role: security.valuerAccessOtherContactType });
          const valuerAccessOtherCompanyParty: any = {
            id: String(model.parties.length + 1),
            type: 'company',
            tradingName: security.valuerAccessOtherCompanyName,
            name: security.valuerAccessOtherCompanyName,
            contact: String(partyId)
          };

          model.parties.push(valuerAccessOtherCompanyParty);
        }
      }

      // remove address and valuation details if property is not found
      if (security.propertyNotFound || (collateral.address && collateral.address.addressText === 'TBA')) {
        delete collateral.address;
        delete collateral.valuation;
      }

      // add mortgagors
      _.forEach(security.mortgagors, (mortgagor) => {
        const parties = {
          trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
          company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
          person: model.parties.filter((p) => p.type === 'individual') || [],
        };
        const [destination, index] = _.split(mortgagor.to, '-');
        const party = _.get(parties, `${destination}.${index}`);
        if (party) {
          collateral.members.push({
            id: party.id,
            role: MORTGAGOR,
            overrideName: mortgagor.mortgagorName,
          });
        }
      });

      if (security.realEstateAgent && security.realEstateAgent.companyPartyRoleId) {
        const partyId = model.parties.length + 1;
        model.parties.push({
          id: String(model.parties.length + 1),
          type: 'reference',
          referenceType: 'partyRoleId',
          value: parseInt(security.realEstateAgent.companyPartyRoleId, 10),
          roles: ['realEstate'],
        });
        collateral.members.push({ id: String(partyId), role: 'realEstate' });
      }

      if (security.realEstateAgent && security.realEstateAgent.contactPartyRoleId) {
        const partyId = model.parties.length + 1;
        model.parties.push({
          id: String(partyId),
          type: 'reference',
          referenceType: 'partyRoleId',
          value: parseInt(security.realEstateAgent.contactPartyRoleId, 10),
          roles: ['realEstateContact'],
        });
        collateral.members.push({
          id: String(partyId),
          role: 'realEstateContact',
        });
      }

      model.collaterals.push(collateral);
      model.purposes[0].collaterals.push(id);
      model.purposes[0].propertyFound = (!security.propertyNotFound);
    });

    return model;
  }

  private mapPanelSolicitorDetails(widgetValue, model: BaseSubmissionModel) {
    const partyId = model.parties.length + 1;
    if (widgetValue.panelSolicitor) {
      // TODO BRING THIS BACK WHEN READY TO BE FIXED
      model.parties.push({
        id: String(partyId),
        type: 'reference',
        referenceType: 'partyRoleId',
        value: widgetValue.panelSolicitor,
      });
      // add this to the purpose members
      model.purposes[0].members.push({
        id: String(partyId),
        role: 'solicitor',
      });
    }

    // set mortgage docs location
    if (widgetValue.mortgageDocs) {
      model.purposes[0].deliveryOptions = {
        method: widgetValue.mortgageDocs,
        toBroker: widgetValue.sendCopy,
      };
    }

    return model;
  }

  private mapBorrowerSolicitorDetails(widgetValue, model: BaseSubmissionModel) {
    const partyId = model.parties.length + 1;
    if (widgetValue.solicitorBorrower && widgetValue.solicitorBorrower.companyPartyRoleId) {
      // TODO BRING THIS BACK WHEN READY TO BE FIXED
      model.parties.push({
        id: String(partyId),
        type: 'reference',
        referenceType: 'partyRoleId',
        value: widgetValue.solicitorBorrower.companyPartyRoleId,
      });
      // TODO add the contactPartyRoleId
      // add this to the purpose members
      model.purposes[0].members.push({
        id: String(partyId),
        role: 'borrowerSolicitor',
      });
    }

    return model;
  }

  private mapBrokerNotes(widgetValue, model: BaseSubmissionModel) {
    model.purposes[0].notes = widgetValue.notes;
    return model;
  }

  private mapProduct(widgetValue, model: BaseSubmissionModel) {
    // No need to map... -component to hold product data / parameters
    return model;
  }

  private mapInvestmentIncomes(widgetValue: any, model: BaseSubmissionModel) {
    const parties: any = {
      trust: model.parties.filter((p) => p.type === 'company' && p.trust === true) || [],
      company: model.parties.filter((p) => p.type === 'company' && !p.trust) || [],
      person: model.parties.filter((p) => p.type === 'individual') || [],
    };

    // remove contacts from the parties.person array
    if (parties.company.length) {
      _.each(parties.company, (company: any) => {
        const contactIndex: any = _.findIndex(parties.person, { id: company.contact });
        if (contactIndex > -1) {
          parties.person.splice(contactIndex, 1);
        }
      });
    }

    if (parties.trust.length) {
      _.each(parties.trust, (trust: any) => {
        const contactIndex: any = _.findIndex(parties.person, { id: trust.contact });
        if (contactIndex > -1) {
          parties.person.splice(contactIndex, 1);
        }
      });
    }

    console.log('parties', parties);

    _.each(widgetValue.investmentList, (investment: any) => {
      console.log('investmentList', investment);

      const link = uuid.v1();
      const ALEownership = [];

      // process rental incomes
      _.each(investment.parties, (party: any) => {
        let incomeValue = investment.rent;
        const ownership = party.ownership || 0;
        const partyRecord: Party = _.get(parties, `${party.partyType}.${party.index}`);

        // must ensure that there are no contact parties being included in the investment list
        console.log('partyRecord', partyRecord);

        if (partyRecord) {
          const factor = new Decimal(ownership).div(100);
          if (Number(ownership) > 0) {
            ALEownership.push({
              id: partyRecord.id,
              value: factor,
            });
          }
          incomeValue = new Decimal(investment.rent).mul(factor);

          if (!(_.get(partyRecord, 'income'))) {
            partyRecord.income = {};
          }

          if (!(_.get(partyRecord, 'income.rental'))) {
            partyRecord.income.rental = new Array<FrequencyUnitDetails>();
          }

          if (Number(ownership) > 0) {
            partyRecord.income.rental.push({
              amount: incomeValue,
              frequency: investment.frequencyType,
              ownership: new Decimal(ownership).div(100).toNumber(),
              link,
            });
          }
        }
      });

      // process liabilities
      model.servicing[0].liabilities.push({
        type: 'investment',
        amount: Number(investment.monthlyRepayments),
        owing: Number(investment.investmentLoanBalance),
        frequency: 'monthly',
        lender: investment.creditProvider,
        refinancing: investment.refinancing,
        interestOnly: investment.interestOnly,
        isNewInvestment: investment.isNewInvestment,
        invLoanIntAddback: investment.invLoanIntAddback,
        ownership: ALEownership,
        link,
      });

      model.servicing[0].assets.push({
        type: 'investment',
        amount: Number(investment.assetValue),
        description:
          investment.securityAddress && investment.securityAddress.flatSearch
            ? investment.securityAddress.flatSearch
            : investment.address,
        propertyType: investment.propertyType,
        refinancing: investment.refinancing,
        interestOnly: investment.interestOnly,
        useAsSecurity: investment.useAsSecurity,
        securityAddress: investment.securityAddress,
        isHighDensity: investment.isHighDensity,
        isNewInvestment: investment.isNewInvestment,
        ownership: ALEownership,
        rental: true,
        link,
      });
    });
    return model;
  }

  private mapApplicationTypeDetails(widgetValue, model: BaseSubmissionModel) {
    return model;
  }
}
